import React, { FC, useState } from "react";

import {
  Box,
  Toolbar,
  AppBar,
  Grid,
  IconButton,
  Typography,
  Avatar,
  Tooltip,
  Divider,
  useMediaQuery,
  Badge,
  Popover,
} from "@mui/material";
import { Forum as ConversaIcon } from "mdi-material-ui";
import { Menu as MenuIcon } from "@mui/icons-material";

import { useAppSelector, useAppDispatch } from "store";
import { toggleSidebar, toggleRightbar } from "store/modules/layout";
import { useMobile } from "hooks/useMobile";
import icon from "assets/images/icon.png";
import iconDark from "assets/images/icon-dark.png";

import * as styles from "./styles";
import { MensagesUnreads } from "./components/MensagesUnreads";
import { EUserType, IChat, IChatGroup } from "@deltasge/marauders-map";
import { history } from "configs";
import { useLocation } from "react-router-dom";

export const Header: FC<{
  buttons: React.ReactNode[];
  title: string;
  subtitle?: React.ReactNode;
  searchElement?: React.ReactNode;
  hasRightbar?: boolean;
  rightbarIcon?: React.ReactNode;
}> = ({
  buttons,
  title,
  searchElement,
  hasRightbar = false,
  subtitle,
  rightbarIcon,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMd = useMobile("md");
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  const {
    layout: {
      theme: { mode },
    },
    chat: { chat, mensagens, unreads: messages },
  } = useAppSelector((state) => state);

  const isDark = useMediaQuery("(prefers-color-scheme: dark)");

  return (
    <Box sx={styles.root}>
      <AppBar
        position="fixed"
        sx={styles.appBar({ more: isMd ? 0 : 1 })}
        variant="outlined"
        elevation={0}
        color="inherit"
      >
        <Toolbar>
          <Grid container wrap="nowrap" alignItems="center">
            <Grid item>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="Menu"
                onClick={() => dispatch(toggleSidebar())}
              >
                <MenuIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <Avatar
                alt="Delta Software"
                src={
                  mode == "auto"
                    ? isDark
                      ? iconDark
                      : icon
                    : mode == "light"
                    ? icon
                    : iconDark
                }
              />
            </Grid>
            {subtitle && (
              <Grid item xs zeroMinWidth>
                <Typography variant="h6" noWrap>
                  {title}
                </Typography>

                {typeof subtitle == "string" ? (
                  <Typography variant="caption" noWrap component="p">
                    {subtitle}
                  </Typography>
                ) : (
                  subtitle
                )}
              </Grid>
            )}
            {!subtitle && (
              <Grid item xs zeroMinWidth>
                <Typography variant="h6" noWrap>
                  {title}
                </Typography>
              </Grid>
            )}
            {searchElement && (
              <Grid item xs={!isMd} sx={{ flexFlow: !isMd ? "inherit" : 2 }}>
                {searchElement}
              </Grid>
            )}
            {searchElement && (hasRightbar || buttons.length > 0) && (
              <Grid item>
                <Divider sx={styles.divider} orientation="vertical" />
              </Grid>
            )}
            {chat + mensagens > 0 &&
              !pathname.startsWith("/publicacoes") &&
              !pathname.startsWith("/chat") && (
                <Tooltip
                  key="chat-unreads"
                  title={`${chat + mensagens} nova${
                    chat + mensagens > 1 ? "s" : ""
                  } mensage${chat + mensagens > 1 ? "ns" : "m"}`}
                >
                  <IconButton
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    color="inherit"
                  >
                    <Badge color="secondary" badgeContent={chat + mensagens}>
                      <ConversaIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
            {buttons.map((btn, i) => (
              <Grid key={`headerbtn${i}`} item>
                {btn}
              </Grid>
            ))}
            {hasRightbar && (
              <Grid item>
                <Tooltip title="Mostrar ou esconder o menu">
                  <IconButton
                    color="inherit"
                    onClick={() => dispatch(toggleRightbar())}
                  >
                    {rightbarIcon && rightbarIcon}
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Toolbar>
      </AppBar>
      {chat + mensagens > 0 && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MensagesUnreads
            messages={messages}
            onClick={(group: IChatGroup, chat: IChat) => {
              setAnchorEl(null);
              if (group.idMensagem) {
                const user = group.chatUsers?.find(
                  (f) => f.deltaId == chat.fromId
                );
                const idUsuario = user?.id;
                let tab = 2; // aluno
                if (user?.tipoUsuario == EUserType.Responsavel) tab = 3;
                else if (user?.tipoUsuario == EUserType.LoginExtra) tab = 4;
                else if (user?.tipoUsuario == EUserType.Professor) tab = 5;
                history.push(
                  `/publicacoes/detalhes/${group.idMensagem}/tab/${tab}/destinatario/${idUsuario}`
                );
              } else {
                history.push(`/chat/channel/${group._id}`);
              }
            }}
          />
        </Popover>
      )}
    </Box>
  );
};
